import Const from 'constants';
import {resolveExposuresOverlap} from 'helpers/exposure';
import {getTeamNamesOfGames} from 'helpers/game-helper';
import {generateSignature} from 'helpers/hash';

const defaultTeamData = {
    active: false,
    checked: true,
    exposure: {min: 0, max: 100},
};

const defaultStacksData = {
    groups: {
        1: {
            playersQuantity: Const.RANDOM_TEAM_STACKING_MIN_PLAYERS_QUANTITY,
        },
        2: {
            playersQuantity: Const.RANDOM_TEAM_STACKING_MIN_PLAYERS_QUANTITY,
        },
    },
    forceCaptain: true,
    chooseTeamsRandomly: false,
    useDisallowedPositions: false,
    disallowedPositions: {
        1: [],
        2: [],
    },
    exposure: {
        min: 0,
        max: 100,
    },
    checked: true,
    teams: {},
};

const defaultState = {
    enabled: false,
    signature: null,
    data: cp(defaultStacksData),
    isEnabled() {
        return this.enabled;
    },
    getStack() {
        return {...this.data};
    },
    getGroups() {
        return {...this.data.groups};
    },
    getPlayersQuantityByGroupNumber(groupNumber) {
        return Number(this.data.groups[groupNumber].playersQuantity);
    },
    isAllActiveStackTeamsChecked() {
        const teams = this.getActiveTeams();

        for (let team of Object.values(teams)) {
            if (!team.checked) {
                return false;
            }
        }

        return true;
    },
    isCaptainForced() {
        return Boolean(this.data.forceCaptain);
    },
    isTeamsChosenRandomly() {
        return Boolean(this.data.chooseTeamsRandomly);
    },
    isDisallowedPositionsUsed() {
        return Boolean(this.data.useDisallowedPositions);
    },
    getDisallowedPositionsByGroupNumber(groupNumber) {
        return [...this.data.disallowedPositions[groupNumber]];
    },
    getIntersectedDisallowedPositions() {
        return arrayDuplicates(this.data.disallowedPositions[1], this.data.disallowedPositions[2]);
    },
    getStackTeamsExposureByType(exposureType) {
        return this.data.exposure[exposureType];
    },
    getStackTeamsMinExposure() {
        return this.getStackTeamsExposureByType('min');
    },
    getStackTeamsMaxExposure() {
        return this.getStackTeamsExposureByType('max');
    },
    isTeamChecked(team) {
        const stack = this.data;
        if (stack.teams.hasOwnProperty(team) && stack.teams[team].hasOwnProperty('checked')) {
            return stack.teams[team].checked;
        }
        return stack.checked;
    },
    getTeamExposure(team, exposureType) {
        const stack = this.data;
        if (stack.teams.hasOwnProperty(team) && stack.teams[team].hasOwnProperty('exposure')) {
            return stack.teams[team].exposure[exposureType];
        }
        return stack.exposure[exposureType];
    },
    getActiveTeams() {
        const activeTeams = {};

        const {teams} = this.data;

        Object.keys(teams).map((teamName) => {
            if (teams[teamName].active) {
                activeTeams[teamName] = teams[teamName];
            }
        });

        return activeTeams;
    },
    getActiveCheckedTeams() {
        const teams = {};
        Object.keys(this.data.teams).map((teamName) => {
            const team = this.data.teams[teamName];
            if (team.active && team.checked) {
                teams[teamName] = team;
            }
        });
        return {...teams};
    },
    getTeamMinExposure(team) {
        return this.getTeamExposure(team, 'min');
    },
    getTeamMaxExposure(team) {
        return this.getTeamExposure(team, 'max');
    },
    getSignature() {
        return this.signature;
    },
};

export default function (state = defaultState, action) {
    state = {...state};

    if (action.type === Const.RANDOM_TEAM_STACKING_IS_USED) {
        state.enabled = Boolean(action.use);
    } else if (action.type === Const.RANDOM_TEAM_STACKING_SET_TEAMS_ARE_CHOSEN_RANDOMLY) {
        state.data.chooseTeamsRandomly = Boolean(action.chooseTeamsRandomly);
    } else if (action.type === Const.RANDOM_TEAM_STACKING_SET_CAPTAIN_IS_FORCED) {
        state.data.forceCaptain = Boolean(action.forceCaptain);
    } else if (action.type === Const.RANDOM_TEAM_STACKING_SET_DISALLOWED_POSITIONS_IS_USED) {
        state.data.useDisallowedPositions = Boolean(action.useOption);
    } else if (action.type === Const.RANDOM_TEAM_STACKING_SET_DISALLOWED_POSITIONS) {
        state.data.disallowedPositions[action.groupNumber] = [...action.positions];
    } else if (action.type === Const.RANDOM_TEAM_STACKING_SET_PLAYERS_QUANTITY) {
        const group = state.data.groups[action.groupNumber];
        group.playersQuantity = action.playersQuantity;
    } else if (action.type === Const.RANDOM_TEAM_STACKING_SET_TEAMS) {
        const stack = state.data;
        const teams = {};

        Object.keys(stack.teams).map((teamName) => {
            teams[teamName] = stack.teams[teamName];
            teams[teamName].active = inArray(teamName, action.teams);
        });

        stack.teams = teams;
    } else if (action.type === Const.RANDOM_TEAM_STACKING_SET_STACK_TEAMS_IS_CHECKED) {
        const stack = state.data;
        stack.checked = action.checked;
        Object.values(stack.teams).map((team) => {
            team.active && (team.checked = action.checked);
        });
    } else if (action.type === Const.RANDOM_TEAM_STACKING_SET_STACK_TEAMS_EXPOSURE) {
        const stack = state.data;
        Object.keys(action.exposure).map((exposureType) => {
            const value = action.exposure[exposureType];
            stack.exposure[exposureType] = value;

            Object.values(stack.teams).map((team) => {
                const currentExposure = {...team.exposure};
                currentExposure[exposureType] = value;
                team.exposure = resolveExposuresOverlap(currentExposure, exposureType);
            });
        });
    } else if (action.type === Const.RANDOM_TEAM_STACKING_SET_TEAM_IS_CHECKED) {
        const stack = state.data;
        stack.teams[action.team] ??= {...defaultTeamData};
        stack.teams[action.team].checked = Boolean(action.checked);
    } else if (action.type === Const.RANDOM_TEAM_STACKING_SET_TEAM_EXPOSURE) {
        const stack = state.data;
        stack.teams[action.team] ??= {...defaultTeamData};
        stack.teams[action.team].exposure = action.exposure;
    } else if (action.type === Const.GET_GAMES_SUCCESS) {
        const teamNames = getTeamNamesOfGames(action.data);
        const stack = state.data;
        const teams = {};

        teamNames.map((teamName) => {
            teams[teamName] = stack.teams.hasOwnProperty(teamName) ? stack.teams[teamName] : cp(defaultTeamData);
        });

        stack.teams = teams;
    } else if (action.type === Const.RESET_ALL_SETTINGS) {
        const stacksState = cp(defaultStacksData);

        Object.keys(state.data.teams).map((teamName) => {
            stacksState.teams[teamName] = {...defaultTeamData, active: true};
        });

        state = {
            ...defaultState,
            data: stacksState,
            enabled: false,
            signature: generateSignature(),
        };
    } else if (action.type === Const.FILTER_BY_SLATE) {
        state = {
            ...defaultState,
            data: cp(defaultStacksData),
            enabled: false,
            signature: generateSignature(),
        };
    }

    return state;
}
